import {
  request as graphqlRequest,
  rawRequest as rawGraphqlRequest,
  Variables as DefaultVariables,
  RequestOptions,
} from 'graphql-request';

export type { DefaultVariables };

export const defaultUrl =
  process.env.NODE_ENV === 'test'
    ? `${process.env.NEXT_PUBLIC_WEB_ENV}/api/graphql`
    : (process.env.NEXT_PUBLIC_GRAPHQL_API_URL ?? '/api/graphql');

const appendHeader = (
  headers: Headers | [string, string][] | Record<string, string> | undefined,
  key: string,
  value: string,
): Record<string, string> => {
  const headersObj = new Headers(headers);
  headersObj.set(key, value);

  // Convert Headers back to plain object
  const result: Record<string, string> = {};
  headersObj.forEach((value, key) => {
    result[key] = value;
  });
  return result;
};

export type GraphQLRequestProps<Variables extends DefaultVariables = DefaultVariables> =
  RequestOptions<Variables> & {
    url?: string;
    locale: string;
  };

export type GraphQLRequestWithTokenProps<Variables extends DefaultVariables = DefaultVariables> =
  GraphQLRequestProps<Variables> & {
    idToken: string | null;
  };

export const rawRequest = async <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>({
  url = defaultUrl,
  locale,
  ...rest
}: GraphQLRequestProps<Variables>) => {
  if (!locale) {
    return;
  }

  return rawGraphqlRequest<Data, Variables>({
    ...rest,
    url,
    requestHeaders: appendHeader(rest.requestHeaders as any, 'Accept-Language', locale),
  } as any);
};

export const rawRequestWithToken = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>({
  requestHeaders,
  idToken,
  ...rest
}: GraphQLRequestWithTokenProps<Variables>) => {
  return rawRequest<Data, Variables>({
    requestHeaders: idToken
      ? appendHeader(requestHeaders as any, 'Authorization', idToken)
      : (requestHeaders as any),
    ...(rest as unknown as GraphQLRequestProps<Variables>),
  } as any);
};

export const rawRequestWithTokenRequired = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>(
  props: GraphQLRequestWithTokenProps<Variables>,
) => {
  if (!props.idToken) {
    return;
  }
  return rawRequestWithToken<Data, Variables>(props);
};

export const request = <Data = any, Variables extends DefaultVariables = DefaultVariables>({
  url = defaultUrl,
  locale,
  ...rest
}: GraphQLRequestProps<Variables>) => {
  if (!locale) {
    return;
  }

  return graphqlRequest<Data, Variables>({
    ...rest,
    url,
    requestHeaders: {
      'Accept-Language': locale,
      ...rest.requestHeaders,
    },
  } as any);
};

export const requestWithToken = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>({
  requestHeaders,
  idToken,
  ...rest
}: GraphQLRequestWithTokenProps<Variables>) =>
  request<Data, Variables>({
    requestHeaders: idToken ? { ...requestHeaders, Authorization: idToken } : requestHeaders,
    ...(rest as unknown as GraphQLRequestProps<Variables>),
  });

export const requestWithTokenRequired = <
  Data = any,
  Variables extends DefaultVariables = DefaultVariables,
>(
  props: GraphQLRequestWithTokenProps<Variables>,
) => {
  if (!props.idToken) {
    return;
  }
  return requestWithToken<Data, Variables>(props);
};
